<template>
    <button
        v-bind="$attrs"
        ref="buttonRef"
        :disabled="disabled"
        :type="type"
        :class="buttonStyle">
        <slot name="icon">
            <Icon
                v-if="icon"
                :name="icon"
                :class="iconStyle" />
        </slot>
        <slot />
        <span
            v-if="shortcut && useKeymap(shortcut)"
            :class="{'-ml-1 mt-0.5': icon}"
            class="text-xs text-gray-300"> | {{ useKeymap(shortcut) }}
        </span>
    </button>
</template>

<script setup>
defineOptions({
    inheritAttrs: false
})

const props = defineProps({
    //incompatible with deep integration with headlessui. Please only use props.color. Remains for backwards compatibility
    type: {
        type: String,
        default: 'button'
    },
    color: {
        type: String,
        default: 'secondary'
    },
    size: {
        type: String,
        default: 'md'
    },
    icon: {
        type: String,
        default: null
    },
    shortcut: {
        type: String,
        default: null,
        description: 'Define a keyboard shortcut to trigger the button. The label will automatically be appended to the Button title. Example: "meta_enter" -> Button Title | ⌘↵'
    },
    disabled: {
        type: Boolean,
        default: false
    },
    noPadding: {
        type: Boolean,
        default: false
    },
    noShadow: {
        type: Boolean,
        default: false
    },
    outline: {
        type: Boolean,
        default: false
    }
})

const colorStyles = {
    primary: 'bg-primary-600 hover:bg-primary-500 text-white',
    secondary: 'bg-white hover:bg-gray-50 text-gray-900 dark:text-white dark:bg-gray-900 dark:hover:bg-white/20 ring-1 ring-inset ring-gray-300',
    borderless: 'bg-white text-gray-900 dark:text-white dark:bg-gray-900',
    success: 'bg-success-600 hover:bg-success-500 text-white',
    danger: 'bg-danger-600 hover:bg-danger-500 text-white',
    warning: 'bg-warning-600 hover:bg-warning-500 text-white',

    outline: {
        primary: 'bg-transparent text-primary-600 hover:bg-primary-600 hover:text-white dark:bg-gray-900 dark:text-primary-500 dark:hover:bg-white/20 ring-1 ring-inset ring-primary-600',
        secondary: 'bg-transparent text-gray-900 hover:bg-gray-50 dark:text-white dark:bg-gray-900 dark:hover:bg-white/20 ring-1 ring-inset ring-gray-300',
        borderless: 'bg-transparent text-gray-900 dark:text-white dark:bg-gray-900',
        danger: 'bg-transparent text-danger-600 hover:bg-danger-600 hover:text-white dark:bg-gray-900 dark:text-danger-500 dark:hover:bg-white/20 ring-1 ring-inset ring-danger-600',
        warning: 'bg-transparent text-warning-600 hover:bg-warning-600 hover:text-white dark:bg-gray-900 dark:text-warning-500 dark:hover:bg-white/20 ring-1 ring-inset ring-warning-600'
    }
}

const roundedStyles = {
    xs: 'rounded-sm',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-md',
    xl: 'rounded-md'
}

const textStyles = {
    xs: 'text-xs',
    sm: 'text-sm',
    md: 'text-sm',
    lg: 'text-sm',
    xl: 'text-sm'
}

const paddingStyles = {
    xs: 'px-2 py-1',
    sm: 'px-2 py-1',
    md: 'px-2.5 py-1.5',
    lg: 'px-3 py-2',
    xl: 'px-3.5 py-2.5'
}

const iconSizingStyles = {
    xs: '-mr-0.5 size-3',
    sm: '-mr-0.5 size-4',
    md: '-mr-0.5 size-5',
    lg: '-mr-0.5 size-6',
    xl: '-mr-0.5 size-6',
}

const buttonStyle = computed(() => {
    const styles = ['font-semibold']

    // fallback to props.type if color is not defined
    styles.push((props.outline ? colorStyles.outline : colorStyles)[props.color ? props.color : props.type])
    styles.push(textStyles[props.size])

    if (!props.noPadding)
        styles.push(paddingStyles[props.size])

    if (!props.noShadow)
        styles.push('shadow-xs')

    if (props.round) {
        styles.push('rounded-full')
    } else {
        styles.push(roundedStyles[props.size])
    }

    if (props.disabled) {
        styles.push('opacity-50 cursor-not-allowed')
    } else {
        styles.push('cursor-pointer')
    }

    if (props.icon) {
        styles.push('flex items-center justify-center gap-2')
    }

    return styles.join(' ')
})

const iconStyle = computed(() => {
    return iconSizingStyles[props.size]
})

const buttonRef = ref(null)

onMounted(() => {
    if (props.shortcut) {
        useShortcuts({
            [props.shortcut]: {
                handler: () => {
                    buttonRef?.value?.click()
                },
                disabled: () => props.disabled
            }
        })
    }
})
</script>
